import React, { useState, useEffect, useMemo } from "react";
import { Building2, Save, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { updateLogisticsCompany } from "../../../../api/settings";
import { showToast } from "../../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../../utils/apiErrorMessage";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Input } from "../../../../components/ui/Input";
import { Button } from "../../../../components/ui/Button";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";

// `error` reemplaza al hint: cuando el backend rechazó ESTE campo, explicar el
// formato esperado encima del motivo real solo distrae.
function Field({ label, hint, error, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-sm font-medium text-fg">{label}</label>
      {children}
      {error ? (
        <span className="text-xs text-status-error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="text-xs text-fg-muted">{hint}</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección Organización
// ─────────────────────────────────────────────────────────────────────────────

// Campos que el endpoint PATCH /logistics-companies/:id persiste realmente.
// commercialContactEmail y website devuelven 200 pero el backend los ignora →
// se muestran como solo lectura, no se envían.
const DEFAULT_TZ = "America/Santiago";

const FIELDS = [
  "legalName",
  "name",
  "businessIdentifier",
  "fiscalAddress",
  "timezone",
  "helpDesk",
];

// Teléfono de soporte: mismo criterio que valida microservice-users al persistir
// (E.164, o vacío para no publicar ninguno). Se replica acá para que un formato
// mal escrito se avise en el campo y no vuelva como un 400 con un toast genérico.
// El backend sigue siendo la autoridad: acá solo se adelanta el mensaje.
const HELP_DESK_PATTERN = /^(|\+[1-9]\d{7,14})$/;
const normalizeHelpDesk = (value) => String(value ?? "").replace(/[\s().-]/g, "");

// Zonas horarias soportadas (debe coincidir con ALLOWED_TIMEZONES del bff).
const TIMEZONE_OPTIONS = [
  { value: "America/Santiago", label: "Chile — Santiago (GMT-4/-3)" },
  { value: "America/Lima", label: "Perú — Lima (GMT-5)" },
  { value: "America/Bogota", label: "Colombia — Bogotá (GMT-5)" },
  { value: "America/Argentina/Buenos_Aires", label: "Argentina — Buenos Aires (GMT-3)" },
  { value: "America/Sao_Paulo", label: "Brasil — São Paulo (GMT-3)" },
  { value: "America/Mexico_City", label: "México — Ciudad de México (GMT-6)" },
];

const formFrom = (src) =>
  Object.fromEntries(
    FIELDS.map((k) => [
      k,
      k === "timezone" ? src?.timezone || DEFAULT_TZ : src?.[k] ?? "",
    ]),
  );

export default function OrganizacionSection({ company, companyId, onCompanyUpdate }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => formFrom(null));
  const [saving, setSaving] = useState(false);
  // Motivo por el que el backend rechazó el identificador tributario (hoy, el 409
  // de "ya está en uso"). Es del CAMPO, no del formulario: se ancla bajo el input
  // para que el operador sepa qué corregir sin releer el toast.
  const [businessIdentifierError, setBusinessIdentifierError] = useState(null);

  // Cargar valores reales de la empresa cuando llegan/cambian.
  useEffect(() => {
    if (company) setForm(formFrom(company));
  }, [company]);

  const dirty = useMemo(
    () =>
      !!company &&
      FIELDS.some((k) => {
        const orig = k === "timezone" ? company.timezone || DEFAULT_TZ : company[k] ?? "";
        return (form[k] ?? "") !== orig;
      }),
    [form, company],
  );

  const set = (k) => (e) => {
    // El 409 describe el valor que se acaba de reemplazar: al editarlo deja de
    // aplicar, así que el aviso se limpia en vez de quedar pegado al campo.
    if (k === "businessIdentifier") setBusinessIdentifierError(null);
    setForm((p) => ({ ...p, [k]: e.target.value }));
  };

  const helpDeskInvalid = useMemo(
    () => !HELP_DESK_PATTERN.test(normalizeHelpDesk(form.helpDesk)),
    [form.helpDesk],
  );

  const handleReset = () => {
    if (company) setForm(formFrom(company));
    setBusinessIdentifierError(null);
  };

  const handleSave = async () => {
    if (!companyId || !dirty || helpDeskInvalid) return;
    setSaving(true);
    // Un guardado nuevo parte sin el veredicto del anterior.
    setBusinessIdentifierError(null);
    try {
      const payload = Object.fromEntries(FIELDS.map((k) => [k, form[k]]));
      const res = await updateLogisticsCompany(companyId, payload);
      const updated = res?.data ?? res ?? {};
      onCompanyUpdate?.({ ...company, ...payload, ...updated });
      showToast(t("Datos de la empresa actualizados"), true);
    } catch (err) {
      // microservice-users ya explica el motivo (409 del identificador en uso,
      // 400 de validación); descartarlo dejaba al operador con un genérico que
      // no dice qué corregir.
      const message = apiErrorMessage(
        err,
        t("No se pudieron guardar los datos de la empresa"),
      );
      showToast(message, false);
      // Solo el 409 apunta a UN campo de este formulario: se muestra además
      // bajo el input, que es donde el operador va a ir a arreglarlo. Lleva su
      // propio texto por defecto porque el del toast ("no se pudieron guardar")
      // no dice qué campo está mal.
      if (apiErrorStatus(err) === 409) {
        setBusinessIdentifierError(
          apiErrorMessage(err, t("Este identificador ya está en uso por otra empresa.")),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const idType = company?.businessIdentifierType || "RUT";

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      {/* ── Datos de la empresa (CONECTADO) ─────────────────────────────── */}
      <SectionCard
        icon={<Building2 className="w-4 h-4" aria-hidden="true" />}
        title={t("Datos de la empresa")}
        subtitle={t("Información que ven tus comercios y aparece en documentos.")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("Razón social")}>
            <Input value={form.legalName} onChange={set("legalName")} disabled={saving} />
          </Field>
          <Field label={t("Nombre comercial")}>
            <Input value={form.name} onChange={set("name")} disabled={saving} />
          </Field>
          <Field
            label={t("{{type}} / ID tributario", { type: idType })}
            error={businessIdentifierError}
          >
            <Input
              value={form.businessIdentifier}
              onChange={set("businessIdentifier")}
              disabled={saving}
              aria-invalid={businessIdentifierError ? true : undefined}
            />
          </Field>
          <Field label={t("Email comercial")} hint={t("Solo lectura — se gestiona fuera de este panel.")}>
            <Input type="email" value={company?.commercialContactEmail || ""} readOnly disabled />
          </Field>
          {/* Este número se le publica al CLIENTE FINAL del comercio: sale como
              botón de llamar en el seguimiento público del pedido y dentro del
              WhatsApp de entrega fallida. El hint lo dice porque no es obvio
              desde una pantalla titulada "Datos de la empresa". */}
          <Field
            label={t("Teléfono de soporte")}
            hint={
              helpDeskInvalid
                ? t("Formato inválido. Incluí el código de país, por ejemplo +56912345678.")
                : t("Se lo mostramos al cliente final en el seguimiento del pedido y en los avisos de entrega fallida. Con código de país; dejalo vacío para no publicar ninguno.")
            }
          >
            <Input
              type="tel"
              value={form.helpDesk}
              onChange={set("helpDesk")}
              disabled={saving}
              placeholder="+56912345678"
              aria-invalid={helpDeskInvalid || undefined}
            />
          </Field>
          <Field label={t("Sitio web")} hint={t("Solo lectura — se gestiona fuera de este panel.")}>
            <Input value={company?.website || ""} readOnly disabled placeholder="—" />
          </Field>
          <Field label={t("Dirección fiscal")} className="md:col-span-2">
            <Input value={form.fiscalAddress} onChange={set("fiscalAddress")} disabled={saving} />
          </Field>
          <Field label={t("País de operación")} hint={t("Definido por la cuenta; no editable aquí.")}>
            <div className="h-10 w-full rounded-lg border border-border bg-bg px-3 flex items-center gap-2 text-sm text-fg-muted">
              <Globe className="w-4 h-4 text-fg-subtle" aria-hidden="true" />
              {company?.operationsCountry || "—"}
            </div>
          </Field>
        </div>
      </SectionCard>

      {/* ── Configuración regional ──────────────────────────────────────── */}
      <SectionCard
        title={t("Configuración regional")}
        subtitle={t("Zona horaria, idioma y formatos.")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label={t("Zona horaria")}
            hint={t("Usada para los cortes por día (p. ej. paquetes en ruta hoy).")}
          >
            <select
              value={form.timezone}
              onChange={set("timezone")}
              disabled={saving}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
            >
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.label)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <PendingBackendNotice>{t("Idioma y formatos — en construcción.")}</PendingBackendNotice>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60 pointer-events-none select-none" aria-disabled="true">
          <Field label={t("Idioma por defecto")}>
            <Input value={t("Español de Chile")} readOnly disabled />
          </Field>
          <Field label={t("Moneda")}>
            <Input value={t("CLP — Peso chileno")} readOnly disabled />
          </Field>
          <Field label={t("Formato de fecha")}>
            <Input value="26-04-2026 (DD-MM-YYYY)" readOnly disabled />
          </Field>
        </div>
      </SectionCard>

      {/* ── Barra de guardado ───────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          {t("Cancelar")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!dirty || saving || helpDeskInvalid}
          loading={saving}
          iconLeft={<Save size={16} />}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
